import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
    PawPrint, Camera, ArrowRight, ArrowLeft, CheckCircle, Info,
    Sparkle, Heart, House, ShieldCheck
} from "@phosphor-icons/react";
import Nav from "@/components/Nav";
import api, { fileUrl, formatApiError } from "@/lib/api";
import AudioRecorder from "@/components/AudioRecorder";
import AnimatedPuppy from "@/components/AnimatedPuppy";
import Confetti from "@/components/Confetti";
import QRCard from "@/components/QRCard";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useHandlePremiumGate } from "@/lib/premiumGate";
import { track } from "@/lib/analytics";

const DEFAULT_PRIVACY = {
    show_phone: true, show_owner_name: true, show_address: false,
    show_city_only: true, show_medical: true, show_allergies: true,
};

const TOTAL_STEPS = 5;
const FRONTEND_BASE = typeof window !== "undefined" ? window.location.origin : "";
const finderUrl = (publicId) => `${FRONTEND_BASE}/p/${publicId}`;

export default function PetForm() {
    const { id } = useParams();
    const isEdit = Boolean(id);
    const nav = useNavigate();
    const { user } = useAuth();
    const { t } = useI18n();
    const openPremiumGate = useHandlePremiumGate(user);

    const [form, setForm] = useState({
        name: "", type: "dog", breed: "", gender: "",
        photo_path: null,
        owner_name: user?.name || "", owner_phone: "", owner_whatsapp: "",
        address: "", city: "",
        allergies: "", medical_notes: "",
        finder_message: t("petform.default_msg"),
        audio_path: null,
        privacy: DEFAULT_PRIVACY,
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [audioClearedFlag, setAudioClearedFlag] = useState(false);

    const [step, setStep] = useState(1); // 1..5, then 6 = success
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(isEdit);
    const [savedPet, setSavedPet] = useState(null); // pet object after create/update

    const stepScrollRef = useRef(null);

    useEffect(() => {
        if (!isEdit) return;
        (async () => {
            try {
                const { data } = await api.get(`/pets/${id}`);
                setForm({
                    ...form,
                    ...data,
                    breed: data.breed || "",
                    gender: data.gender || "",
                    privacy: { ...DEFAULT_PRIVACY, ...(data.privacy || {}) },
                });
            } catch (e) {
                setError(formatApiError(e.response?.data?.detail) || "Could not load pet");
            } finally { setLoading(false); }
        })();
    }, [id, isEdit]);

    useEffect(() => {
        // scroll to top of the step container on step change
        if (stepScrollRef.current) {
            stepScrollRef.current.scrollTo?.({ top: 0, behavior: "smooth" });
        }
        try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) { /* ignore */ }
    }, [step]);

    const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const setPrivacy = (k, v) => setForm((f) => ({ ...f, privacy: { ...f.privacy, [k]: v } }));

    const onPhotoPick = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setPhotoFile(f);
    };

    const canContinueFromStep = (s) => {
        if (s === 1) return form.name.trim().length > 0 && !!form.type;
        if (s === 2) return form.owner_phone.trim().length > 0;
        return true;
    };

    const goNext = () => {
        setError("");
        if (!canContinueFromStep(step)) {
            if (step === 1) setError("Please give your pet a name.");
            if (step === 2) setError("Phone number is required so a finder can reach you.");
            return;
        }
        if (step < TOTAL_STEPS) setStep(step + 1);
        else submit();
    };
    const goBack = () => { setError(""); if (step > 1) setStep(step - 1); };

    const submit = async () => {
        setError(""); setSaving(true);
        try {
            let photoPath = form.photo_path;
            if (photoFile) {
                const fd = new FormData();
                fd.append("file", photoFile);
                const { data } = await api.post("/upload/photo", fd);
                photoPath = data.path;
            }
            let audioPath = form.audio_path;
            if (audioClearedFlag) audioPath = null;
            if (audioFile) {
                const fd = new FormData();
                fd.append("file", audioFile);
                const { data } = await api.post("/upload/audio", fd);
                audioPath = data.path;
            }
            const payload = { ...form, photo_path: photoPath, audio_path: audioPath };
            let saved;
            if (isEdit) {
                const { data } = await api.put(`/pets/${id}`, payload);
                saved = data;
                track("edit_pet", { pet_id: saved.id, pet_kind: saved.kind });
            } else {
                const { data } = await api.post("/pets", payload);
                saved = data;
                track("create_pet", { pet_id: saved.id, pet_kind: saved.kind });
            }
            setSavedPet(saved);
            setStep(6); // success
        } catch (e) {
            // Freemium gate on the 2nd+ pet 402 from POST /pets. The
            // backend entitlement is NOT bypassed; only the UI response
            // changes:
            //   • free store-app user → "Premium is coming soon" modal
            //   • free web user       → routed to /subscription (Paddle)
            //   • real Premium user   → shouldn't hit 402 in the first place
            // If the gate handles it, we clear the raw error text so the
            // user never sees the underlying 402.
            if (e?.response?.status === 402 && openPremiumGate("extra_pet")) {
                setError("");
            } else {
                setError(formatApiError(e.response?.data?.detail) || "Could not save");
            }
        } finally { setSaving(false); }
    };

    if (loading) return (
        <div className="min-h-screen bg-paw-bone dark:bg-[hsl(152,15%,8%)]">
            <Nav />
            <div className="pt-32 text-center text-paw-ink2 dark:text-paw-bone/60" data-testid="petform-loading">{t("common.loading")}</div>
        </div>
    );

    if (step === 6 && savedPet) {
        return <SuccessScreen pet={savedPet} photoFile={photoFile} onDashboard={() => nav("/home")} />;
    }

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-[hsl(152,15%,8%)] text-paw-ink dark:text-paw-bone">
            <Nav />
            <div ref={stepScrollRef} className="pt-24 pb-32 max-w-3xl mx-auto px-4 sm:px-6">
                {/* Back link (edit resume) */}
                <Link to="/home" data-testid="petform-back"
                    className="inline-flex items-center gap-2 text-sm text-paw-ink2 dark:text-paw-bone/60 hover:text-paw-ink dark:hover:text-paw-bone mb-8 transition">
                    <ArrowLeft size={16} /> {t("petform.back_to_dash")}
                </Link>

                {/* Step progress */}
                <StepProgress step={step} total={TOTAL_STEPS} t={t} />

                <form onSubmit={(e) => { e.preventDefault(); goNext(); }} className="mt-6" data-testid="petform" noValidate>
                    <div key={step} className="wizard-step-in">
                        {step === 1 && (
                            <Step1 form={form} setField={setField} photoFile={photoFile} onPhotoPick={onPhotoPick} clearPhoto={() => { setPhotoFile(null); setField("photo_path", null); }} t={t} />
                        )}
                        {step === 2 && (
                            <Step2 form={form} setField={setField} t={t} />
                        )}
                        {step === 3 && (
                            <Step3 form={form} setField={setField} t={t} />
                        )}
                        {step === 4 && (
                            <Step4 form={form} setField={setField}
                                audioFile={audioFile} onAudioFile={(f) => { setAudioFile(f); setAudioClearedFlag(f === null); }}
                                t={t} />
                        )}
                        {step === 5 && (
                            <Step5 form={form} setPrivacy={setPrivacy} t={t} />
                        )}
                    </div>

                    {error && (
                        <div data-testid="petform-error" className="mt-6 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 text-sm">
                            {error}
                        </div>
                    )}

                    {/* Sticky nav footer */}
                    <div className="mt-8 sticky bottom-4 z-10">
                        <div className="card-soft p-3 sm:p-4 flex items-center gap-3 justify-between shadow-hover backdrop-blur bg-white/85 dark:bg-[hsl(152,12%,10%)]/85">
                            <button
                                type="button"
                                onClick={step === 1 ? () => nav("/home") : goBack}
                                data-testid="petform-back-btn"
                                className="btn-ghost !py-2.5 !px-4 text-sm"
                            >
                                <ArrowLeft size={16} />
                                <span className="ms-1.5">{step === 1 ? t("common.cancel") : t("petform.back")}</span>
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                data-testid={step === TOTAL_STEPS ? "petform-submit" : "petform-continue"}
                                className="btn-primary !py-3 !px-6 text-sm disabled:opacity-60"
                            >
                                {step < TOTAL_STEPS ? (
                                    <>
                                        <span>{t("petform.continue")}</span>
                                        <ArrowRight size={16} className="ms-1.5" />
                                    </>
                                ) : (
                                    <>
                                        <ShieldCheck size={18} weight="fill" />
                                        <span className="ms-1.5">
                                            {saving
                                                ? t("common.saving")
                                                : (form.name
                                                    ? t("petform.protect_btn", { name: form.name })
                                                    : t("petform.protect_generic"))}
                                        </span>
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
}

/* ------------------- Step progress ------------------- */
function StepProgress({ step, total, t }) {
    const activeIdx = Math.min(step, total);
    return (
        <div data-testid="petform-progress">
            <p className="label-soft uppercase text-paw-ink2 dark:text-paw-bone/60" data-testid="petform-step-label">
                {t("petform.step_of", { n: activeIdx, total })}
            </p>
            <div className="mt-3 flex items-center gap-2">
                {Array.from({ length: total }).map((_, i) => {
                    const idx = i + 1;
                    const state = idx < activeIdx ? "done" : idx === activeIdx ? "current" : "todo";
                    return (
                        <div key={`step-${i}`} className="flex-1 h-1.5 rounded-full overflow-hidden bg-paw-line/70 dark:bg-white/10">
                            <div className={`h-full rounded-full transition-all duration-500 ${
                                state === "done" ? "bg-paw-forest dark:bg-paw-terra w-full"
                                : state === "current" ? "bg-paw-terra w-2/3"
                                : "w-0"
                            }`} />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ------------------- Section header ------------------- */
function StepHead({ emoji, title, sub }) {
    return (
        <header className="mb-6">
            <div className="text-4xl sm:text-5xl mb-2" aria-hidden>{emoji}</div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-[44px] font-medium tracking-tight text-paw-ink dark:text-paw-bone">
                {title}
            </h1>
            {sub && <p className="mt-3 text-paw-ink2 dark:text-paw-bone/60 text-base max-w-xl">{sub}</p>}
        </header>
    );
}

/* ------------------- Step 1 — Meet your best friend ------------------- */
function Step1({ form, setField, photoFile, onPhotoPick, clearPhoto, t }) {
    const photoPreview = photoFile ? URL.createObjectURL(photoFile) : form.photo_path ? fileUrl(form.photo_path) : null;

    return (
        <section data-testid="petform-step-1">
            <StepHead emoji={t("petform.s1_emoji")} title={t("petform.s1_h")} sub={t("petform.s1_sub")} />

            <div className="card-soft p-6 sm:p-10">
                {/* Premium photo circle */}
                <div className="flex flex-col items-center text-center">
                    <label className="relative group cursor-pointer photo-drop rounded-full" data-testid="petform-photo-drop">
                        <div className="w-44 h-44 sm:w-52 sm:h-52 rounded-full bg-paw-muted dark:bg-white/5 overflow-hidden grid place-items-center text-paw-forest dark:text-paw-terra ring-4 ring-white dark:ring-[hsl(152,12%,10%)] shadow-hover">
                            {photoPreview ? (
                                <img src={photoPreview} alt="pet" className="w-full h-full object-cover" data-testid="petform-photo-preview" />
                            ) : (
                                <PawPrint size={64} weight="duotone" />
                            )}
                        </div>
                        {/* Overlay */}
                        <div className="absolute inset-0 rounded-full grid place-items-center bg-paw-ink/0 group-hover:bg-paw-ink/45 transition-colors duration-300">
                            <div className={`transition-all duration-300 flex flex-col items-center gap-1 ${photoPreview ? "opacity-0 group-hover:opacity-100" : "opacity-0"}`}>
                                <Camera size={22} weight="fill" className="text-white" />
                                <span className="text-xs font-medium text-white">{t("petform.s1_change_photo")}</span>
                            </div>
                        </div>
                        {/* Camera badge */}
                        <div className="absolute bottom-2 end-2 w-11 h-11 rounded-full bg-paw-terra text-white grid place-items-center shadow-hover ring-4 ring-paw-bone dark:ring-[hsl(152,15%,8%)]">
                            <Camera size={18} weight="fill" />
                        </div>
                        <input data-testid="petform-photo-input" type="file" accept="image/*" className="hidden" onChange={onPhotoPick} />
                    </label>

                    {!photoPreview && (
                        <p className="mt-4 font-display text-lg text-paw-ink dark:text-paw-bone">{t("petform.s1_photo_label")}</p>
                    )}
                    <p className={`mt-2 text-sm text-paw-ink2 dark:text-paw-bone/60 max-w-xs ${photoPreview ? "" : "mt-1"}`}>
                        {t("petform.s1_photo_hint")}
                    </p>

                    {photoPreview && (
                        <button type="button" onClick={clearPhoto}
                            className="mt-3 text-xs text-red-600 hover:underline" data-testid="petform-photo-clear">
                            {t("common.remove")}
                        </button>
                    )}
                </div>

                {/* Name */}
                <div className="mt-10">
                    <label className="label-soft">{t("petform.pet_name")}</label>
                    <input data-testid="petform-name" required
                        className="input-soft mt-2 text-lg"
                        value={form.name}
                        onChange={(e) => setField("name", e.target.value)}
                        placeholder={t("petform.s1_name_ph")} />
                </div>

                {/* Type — chip segmented */}
                <div className="mt-6">
                    <label className="label-soft">{t("petform.type")}</label>
                    <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" data-testid="petform-type-group">
                        {[
                            { v: "dog", label: `🐶  ${t("petform.type_dog")}` },
                            { v: "cat", label: `🐱  ${t("petform.type_cat")}` },
                            { v: "other", label: `🐾  ${t("petform.type_other")}` },
                        ].map((opt) => (
                            <button
                                key={opt.v}
                                type="button"
                                data-testid={`petform-type-${opt.v}`}
                                aria-selected={form.type === opt.v}
                                onClick={() => setField("type", opt.v)}
                                className="chip-option"
                            >{opt.label}</button>
                        ))}
                    </div>
                </div>

                {/* Breed & Gender */}
                <div className="mt-6 grid sm:grid-cols-2 gap-5">
                    <div>
                        <label className="label-soft">{t("petform.s1_breed_label")}</label>
                        <input data-testid="petform-breed" className="input-soft mt-2"
                            value={form.breed || ""} onChange={(e) => setField("breed", e.target.value)}
                            placeholder={t("petform.s1_breed_ph")} />
                    </div>
                    <div>
                        <label className="label-soft">{t("petform.s1_gender_label")}</label>
                        <div className="mt-2 flex flex-wrap gap-2" role="radiogroup" data-testid="petform-gender-group">
                            {[
                                { v: "male", label: t("petform.gender_male") },
                                { v: "female", label: t("petform.gender_female") },
                                { v: "", label: t("petform.gender_unknown") },
                            ].map((opt) => (
                                <button
                                    key={opt.v || "unset"}
                                    type="button"
                                    data-testid={`petform-gender-${opt.v || "unset"}`}
                                    aria-selected={(form.gender || "") === opt.v}
                                    onClick={() => setField("gender", opt.v)}
                                    className="chip-option"
                                >{opt.label}</button>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

/* ------------------- Step 2 — Help them get home ------------------- */
function Step2({ form, setField, t }) {
    return (
        <section data-testid="petform-step-2">
            <StepHead emoji={t("petform.s2_emoji")} title={t("petform.s2_h")} sub={t("petform.s2_sub")} />
            <div className="card-soft p-6 sm:p-8 grid sm:grid-cols-2 gap-5">
                <div>
                    <label className="label-soft">{t("petform.owner_name")}</label>
                    <input data-testid="petform-owner-name" className="input-soft mt-2"
                        value={form.owner_name || ""} onChange={(e) => setField("owner_name", e.target.value)} />
                </div>
                <div>
                    <label className="label-soft">{t("petform.phone")}</label>
                    <input data-testid="petform-owner-phone" required className="input-soft mt-2"
                        value={form.owner_phone || ""} onChange={(e) => setField("owner_phone", e.target.value)}
                        placeholder="+1 555 123 4567" />
                </div>
                <div>
                    <label className="label-soft">{t("petform.whatsapp")}</label>
                    <input data-testid="petform-whatsapp" className="input-soft mt-2"
                        value={form.owner_whatsapp || ""} onChange={(e) => setField("owner_whatsapp", e.target.value)}
                        placeholder="+1 555 123 4567" />
                </div>
                <div>
                    <label className="label-soft">{t("petform.city")}</label>
                    <input data-testid="petform-city" className="input-soft mt-2"
                        value={form.city || ""} onChange={(e) => setField("city", e.target.value)}
                        placeholder="San Francisco" />
                </div>
                <div className="sm:col-span-2">
                    <label className="label-soft flex items-center gap-2">
                        {t("petform.address")}
                    </label>
                    <input data-testid="petform-address" className="input-soft mt-2"
                        value={form.address || ""} onChange={(e) => setField("address", e.target.value)} />
                    <p className="mt-2 text-xs text-paw-ink2 dark:text-paw-bone/50 inline-flex items-center gap-1.5">
                        <Info size={13} /> {t("petform.s2_privacy_note")}
                    </p>
                </div>
            </div>
        </section>
    );
}

/* ------------------- Step 3 — Things a rescuer should know ------------------- */
function Step3({ form, setField, t }) {
    return (
        <section data-testid="petform-step-3">
            <StepHead emoji={t("petform.s3_emoji")} title={t("petform.s3_h")} sub={t("petform.s3_sub")} />
            <div className="card-soft p-6 sm:p-8 space-y-5">
                <div>
                    <label className="label-soft">{t("petform.allergies")}</label>
                    <textarea data-testid="petform-allergies" rows={3} className="input-soft mt-2"
                        value={form.allergies || ""} onChange={(e) => setField("allergies", e.target.value)}
                        placeholder={t("petform.allergies_ph")} />
                </div>
                <div>
                    <label className="label-soft">{t("petform.medical")}</label>
                    <textarea data-testid="petform-medical" rows={3} className="input-soft mt-2"
                        value={form.medical_notes || ""} onChange={(e) => setField("medical_notes", e.target.value)}
                        placeholder={t("petform.medical_ph")} />
                </div>
                <div>
                    <label className="label-soft">{t("petform.s3_notes_label")}</label>
                    <textarea data-testid="petform-notes" rows={2} className="input-soft mt-2"
                        value={form.finder_message || ""} onChange={(e) => setField("finder_message", e.target.value)}
                        placeholder={t("petform.s3_notes_ph")} />
                </div>
            </div>
        </section>
    );
}

/* ------------------- Step 4 — Voice message from the heart ------------------- */
function Step4({ form, setField, onAudioFile, t }) {
    return (
        <section data-testid="petform-step-4">
            <StepHead emoji={t("petform.s4_emoji")} title={t("petform.s4_h")} sub={t("petform.s4_sub")} />

            <div className="card-soft p-6 sm:p-8 space-y-6">
                {/* Emotional example bubble */}
                <div className="rounded-2xl bg-paw-terra/8 dark:bg-paw-terra/12 border border-paw-terra/25 px-5 py-4 flex items-start gap-3">
                    <Heart size={20} weight="fill" className="text-paw-terra mt-0.5 flex-shrink-0" />
                    <p className="text-sm sm:text-base text-paw-ink dark:text-paw-bone leading-relaxed italic">
                        {t("petform.s4_example")}
                    </p>
                </div>

                {/* Voice recorder */}
                <div data-testid="petform-voice">
                    <AudioRecorder
                        existingUrl={form.audio_path ? fileUrl(form.audio_path) : null}
                        onFile={onAudioFile}
                    />
                </div>

                {/* Written message */}
                <div>
                    <label className="label-soft">{t("petform.s4_written_label")}</label>
                    <textarea data-testid="petform-finder-message" rows={3} className="input-soft mt-2"
                        value={form.finder_message || ""}
                        onChange={(e) => setField("finder_message", e.target.value)} />
                </div>
            </div>
        </section>
    );
}

/* ------------------- Step 5 — Privacy control ------------------- */
function Step5({ form, setPrivacy, t }) {
    const rows = [
        { k: "show_phone", label: t("petform.p_phone") },
        { k: "show_owner_name", label: t("petform.p_name") },
        { k: "show_city_only", label: t("petform.p_city_only") },
        { k: "show_address", label: t("petform.p_address") },
        { k: "show_allergies", label: t("petform.p_allergies") },
        { k: "show_medical", label: t("petform.p_medical") },
    ];
    const subKey = form.name ? t("petform.s5_sub", { name: form.name }) : t("petform.s5_sub_generic");
    return (
        <section data-testid="petform-step-5">
            <StepHead emoji={t("petform.s5_emoji")} title={t("petform.s5_h")} sub={subKey} />
            <div className="card-soft p-6 sm:p-8 divide-y divide-paw-line dark:divide-white/10">
                {rows.map((row) => (
                    <label key={row.k}
                        data-testid={`petform-privacy-${row.k}`}
                        className="flex items-center justify-between gap-4 py-4 first:pt-0 last:pb-0 cursor-pointer">
                        <span className="text-paw-ink dark:text-paw-bone text-sm sm:text-base">{row.label}</span>
                        <input
                            type="checkbox"
                            className="pl-switch"
                            checked={Boolean(form.privacy[row.k])}
                            onChange={(e) => setPrivacy(row.k, e.target.checked)}
                        />
                    </label>
                ))}
            </div>
        </section>
    );
}

/* ------------------- Success screen ------------------- */
function SuccessScreen({ pet, photoFile, onDashboard }) {
    const { t } = useI18n();
    const photoSrc = useMemo(() => {
        if (pet.photo_path) return fileUrl(pet.photo_path);
        if (photoFile) return URL.createObjectURL(photoFile);
        return null;
    }, [pet.photo_path, photoFile]);
    const url = finderUrl(pet.public_id);

    return (
        <div className="min-h-screen bg-paw-bone dark:bg-[hsl(152,15%,8%)] text-paw-ink dark:text-paw-bone relative overflow-hidden">
            <Nav />
            <Confetti pieces={44} />

            <div className="relative pt-24 pb-24 max-w-3xl mx-auto px-4 sm:px-6">
                {/* Emotional header */}
                <div className="text-center mb-10 relative">
                    <div className="mx-auto w-40 h-40 relative" aria-hidden>
                        <div className="absolute inset-x-4 bottom-3 h-16 rounded-full bg-paw-terra/25 blur-2xl paw-pulse" />
                        <AnimatedPuppy size={160} />
                    </div>
                    <h1 className="mt-2 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-paw-ink dark:text-paw-bone"
                        data-testid="petform-success-h">
                        {pet.name
                            ? `${t("petform.success_h", { name: pet.name })} 🐾`
                            : `${t("petform.success_h_generic")} 🐾`}
                    </h1>
                    <p className="mt-3 text-paw-ink2 dark:text-paw-bone/60 max-w-lg mx-auto">
                        {t("petform.success_sub")}
                    </p>
                </div>

                {/* Digital passport card */}
                <div className="success-pop relative rounded-[2rem] overflow-hidden bg-white dark:bg-[hsl(152,12%,11%)] border border-paw-line dark:border-white/10 shadow-hover"
                    data-testid="petform-success-card">
                    {/* Passport top band */}
                    <div className="relative h-24 bg-gradient-to-br from-paw-forest to-paw-forestDark dark:from-paw-terra dark:to-paw-forest">
                        <div className="absolute inset-0 opacity-25">
                            <svg viewBox="0 0 400 100" preserveAspectRatio="none" className="w-full h-full">
                                <circle cx="60" cy="30" r="18" fill="rgba(255,255,255,0.14)" />
                                <circle cx="340" cy="70" r="24" fill="rgba(255,255,255,0.12)" />
                                <circle cx="200" cy="15" r="12" fill="rgba(255,255,255,0.1)" />
                            </svg>
                        </div>
                        <div className="relative h-full flex items-center justify-between px-6">
                            <div className="inline-flex chip bg-white/15 text-white border-white/10">
                                <ShieldCheck size={12} weight="fill" /> {t("petform.success_card_over")}
                            </div>
                            <span className="text-white/85 font-mono text-xs tracking-wider">
                                {t("petform.success_card_id")} · {pet.public_id?.toUpperCase().slice(0, 6)}
                            </span>
                        </div>
                    </div>

                    <div className="p-6 sm:p-8 grid sm:grid-cols-5 gap-6 items-center">
                        {/* Pet identity */}
                        <div className="sm:col-span-3 flex flex-col items-center sm:items-start text-center sm:text-start">
                            <div className="w-28 h-28 sm:w-32 sm:h-32 rounded-full ring-4 ring-white dark:ring-[hsl(152,12%,10%)] shadow-hover overflow-hidden bg-paw-muted dark:bg-white/5 grid place-items-center text-paw-forest dark:text-paw-terra -mt-16 sm:-mt-20">
                                {photoSrc ? (
                                    <img src={photoSrc} alt={pet.name} className="w-full h-full object-cover" data-testid="petform-success-photo" />
                                ) : (
                                    <PawPrint size={44} weight="duotone" />
                                )}
                            </div>
                            <h2 className="mt-4 font-display text-3xl tracking-tight text-paw-ink dark:text-paw-bone" data-testid="petform-success-name">
                                {pet.name}
                            </h2>
                            <p className="text-sm text-paw-ink2 dark:text-paw-bone/60 capitalize">
                                {t(`petform.type_${pet.type}`)}
                                {pet.breed ? ` · ${pet.breed}` : ""}
                                {pet.city ? ` · ${pet.city}` : ""}
                            </p>
                            <div className="mt-4 inline-flex chip bg-paw-forest/8 dark:bg-paw-terra/12 text-paw-forest dark:text-paw-terra">
                                <Heart size={12} weight="fill" /> Protected by PawLink
                            </div>
                        </div>

                        {/* QR */}
                        <div className="sm:col-span-2 flex justify-center">
                            <QRCard url={url} petName={pet.name} petId={pet.id} photoPath={pet.photo_path} />
                        </div>
                    </div>

                    {/* Actions */}
                    <div className="p-6 sm:p-8 pt-0 grid sm:grid-cols-2 gap-3 border-t border-paw-line dark:border-white/10">
                        <a href={url} target="_blank" rel="noreferrer"
                            data-testid="petform-success-view-profile"
                            className="btn-outline text-sm justify-center">
                            <House size={16} /><span className="ms-1.5">{t("petform.success_view_profile")}</span>
                        </a>
                        <button type="button" onClick={onDashboard}
                            data-testid="petform-success-dashboard"
                            className="btn-primary text-sm justify-center">
                            <CheckCircle size={16} weight="fill" />
                            <span className="ms-1.5">{t("petform.success_go_dashboard")}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
